import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, ArrowLeft, X, TrendingUp, Users, Target } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ScrollArea } from "@/components/ui/scroll-area";
import lifactorLogo from "@/assets/lifactor-logo-black.svg";
import { useLanguageWithOverride, Language } from "@/hooks/use-browser-language";
import { getTranslation } from "@/i18n/social-selling-audit";

const SocialSellingAudit = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language, setLanguage } = useLanguageWithOverride();
  const [step, setStep] = useState(1);
  const [userDomain, setUserDomain] = useState("");
  const [competitorDomains, setCompetitorDomains] = useState<string[]>([]);
  const [newCompetitor, setNewCompetitor] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Translation helper
  const t = (key: Parameters<typeof getTranslation>[1], params?: Record<string, string | number>) => 
    getTranslation(language, key, params);

  const freeEmailProviders = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
    'wp.pl', 'onet.pl', 'o2.pl', 'interia.pl', 'gazeta.pl', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com'
  ];

  const normalizeDomain = (domain: string): string => {
    return domain.trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  };

  const isBusinessEmail = (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    return domain ? !freeEmailProviders.includes(domain) : false;
  };
  
  // Track form start
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('trackCustom', 'SocialSellingAuditStarted', {
        timestamp: new Date().toISOString(),
        language
      });
    }
  }, [language]);

  const toggleLanguage = () => {
    const newLang: Language = language === "pl" ? "en" : "pl";
    setLanguage(newLang);
    // Update URL with lang parameter
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.replaceState({}, "", url.toString());
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    
    if (!userDomain.trim()) {
      newErrors.userDomain = t("errorDomainRequired");
    } else {
      const normalized = normalizeDomain(userDomain);
      if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(normalized)) {
        newErrors.userDomain = t("errorDomainInvalid");
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    // Step 2 with competitors is optional - always valid
    return true;
  };

  const validateStep3 = () => {
    const newErrors: Record<string, string> = {};
    
    if (!email.trim()) {
      newErrors.email = t("errorEmailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("errorEmailInvalid");
    } else if (!isBusinessEmail(email)) {
      newErrors.email = t("errorEmailBusiness");
    }
    
    if (!phone.trim()) {
      newErrors.phone = t("errorPhoneRequired");
    } else if (phone.trim().length < 9) {
      newErrors.phone = t("errorPhoneMin");
    } else if (phone.trim().length > 20) {
      newErrors.phone = t("errorPhoneMax");
    }
    
    if (!consent) {
      newErrors.consent = t("errorConsentRequired");
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Next = () => {
    if (validateStep1()) {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'SocialSellingStepCompleted', {
          step: 1,
          step_name: 'Domain Input',
          domain: normalizeDomain(userDomain)
        });
      }
      setStep(2);
    }
  };

  const handleStep2Next = () => {
    if (validateStep2()) {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'SocialSellingStepCompleted', {
          step: 2,
          step_name: 'Competitors',
          competitors_added: competitorDomains.length
        });
      }
      setStep(3);
    }
  };

  const addCompetitor = () => {
    if (!newCompetitor.trim()) return;
    
    if (competitorDomains.length >= 5) {
      toast({
        title: t("toastLimitTitle"),
        description: t("toastLimitDesc"),
        variant: "destructive"
      });
      return;
    }
    
    const normalized = normalizeDomain(newCompetitor);
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(normalized)) {
      toast({
        title: t("toastInvalidDomainTitle"),
        description: t("toastInvalidDomainDesc"),
        variant: "destructive"
      });
      return;
    }
    
    if (competitorDomains.includes(normalized)) {
      toast({
        title: t("toastDuplicateTitle"),
        description: t("toastDuplicateDesc"),
        variant: "destructive"
      });
      return;
    }
    
    setCompetitorDomains([...competitorDomains, normalized]);
    setNewCompetitor("");
  };

  const removeCompetitor = (index: number) => {
    setCompetitorDomains(competitorDomains.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase
        .from('social_selling_submissions')
        .insert({
          user_domain: normalizeDomain(userDomain),
          competitor_domains: competitorDomains.length > 0 ? competitorDomains : null,
          email: email.trim(),
          phone: phone.trim(),
          status: 'pending'
        })
        .select('id')
        .single();
      
      if (error) throw error;
      
      if (data?.id) {
        // Send Telegram notification (fire and forget - don't block redirect)
        supabase.functions.invoke('notify-social-selling', {
          body: {
            email: email.trim(),
            phone: phone.trim(),
            userDomain: normalizeDomain(userDomain),
            competitorDomains: competitorDomains,
            submissionId: data.id
          }
        }).catch(err => console.error('Telegram notification error:', err));
        
        // Track submission
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('trackCustom', 'SocialSellingAuditSubmitted', {
            submission_id: data.id,
            has_competitors: competitorDomains.length > 0,
            competitors_count: competitorDomains.length,
            language
          });
          
          // Also track as standard Lead event
          window.fbq('track', 'Lead', {
            content_name: 'Social Selling Audit',
            content_category: 'B2B Audit'
          });
        }
        
        toast({
          title: t("toastSuccessTitle"),
          description: t("toastSuccessDesc")
        });
        // Redirect to thank you page with language parameter
        navigate(`/raport-socialselling/thankyou?lang=${language}`);
      }
    } catch (error: any) {
      toast({
        title: t("toastErrorTitle"),
        description: error.message || t("toastErrorDesc"),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressValue = step === 1 ? 33 : step === 2 ? 66 : 100;

  return (
    <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-8 md:py-16">
        <div className="container px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            
            {/* Header with Logo */}
            <div className="flex justify-center items-center mb-8">
              <img src={lifactorLogo} alt="Lifactor" className="h-12 md:h-16" />
            </div>
            
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">
                  {t("stepOf", { step })}
                </span>
                <span className="text-sm text-muted-foreground">{progressValue}%</span>
              </div>
              <Progress value={progressValue} className="h-2" />
            </div>

            {/* Step 1: Domain Input */}
            {step === 1 && (
              <Card className="border-none shadow-2xl animate-fade-in">
                <CardHeader className="text-center pb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 mx-auto">
                    <Target className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-3xl md:text-4xl mb-3">
                    {t("step1Title")}
                  </CardTitle>
                  <CardDescription className="text-base md:text-lg">
                    {t("step1Description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="userDomain" className="text-base">
                      {t("step1DomainLabel")}
                    </Label>
                    <Input
                      id="userDomain"
                      placeholder={t("step1DomainPlaceholder")}
                      value={userDomain}
                      onChange={(e) => setUserDomain(e.target.value)}
                      className={errors.userDomain ? "border-destructive" : ""}
                      onKeyPress={(e) => e.key === 'Enter' && handleStep1Next()}
                    />
                    {errors.userDomain && (
                      <p className="text-sm text-destructive">{errors.userDomain}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {t("step1DomainHint")}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/30">
                      <TrendingUp className="h-6 w-6 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm">{t("step1Feature1Title")}</p>
                        <p className="text-xs text-muted-foreground">{t("step1Feature1Desc")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/30">
                      <Users className="h-6 w-6 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm">{t("step1Feature2Title")}</p>
                        <p className="text-xs text-muted-foreground">{t("step1Feature2Desc")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-secondary/30">
                      <Target className="h-6 w-6 text-primary flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-sm">{t("step1Feature3Title")}</p>
                        <p className="text-xs text-muted-foreground">{t("step1Feature3Desc")}</p>
                      </div>
                    </div>
                  </div>

                  <Button 
                    onClick={handleStep1Next}
                    size="lg" 
                    className="w-full"
                  >
                    {t("next")}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>

                  <LanguageSwitcher language={language} onToggle={toggleLanguage} className="mt-6" />
                </CardContent>
              </Card>
            )}

            {/* Step 2: Competitors Only */}
            {step === 2 && (
              <Card className="border-none shadow-2xl animate-fade-in">
                <CardHeader className="pb-6">
                  <CardTitle className="text-2xl md:text-3xl mb-2">
                    {t("step2Title")}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {t("step2Description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Competitors Section */}
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder={t("step2Placeholder")}
                        value={newCompetitor}
                        onChange={(e) => setNewCompetitor(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addCompetitor()}
                        disabled={competitorDomains.length >= 5}
                      />
                      <Button 
                        onClick={addCompetitor}
                        disabled={competitorDomains.length >= 5}
                        variant="outline"
                      >
                        {t("step2AddButton")}
                      </Button>
                    </div>
                    
                    {competitorDomains.length > 0 && (
                      <div className="space-y-2">
                        {competitorDomains.map((domain, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                          >
                            <span className="text-sm">{domain}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeCompetitor(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-sm text-muted-foreground">
                      {t("step2DomainsAdded", { count: competitorDomains.length })}
                    </p>
                    
                    <p className="text-sm text-muted-foreground italic">
                      {t("step2Optional")}
                    </p>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button 
                      onClick={() => setStep(1)}
                      variant="outline"
                      size="lg"
                      className="w-full"
                    >
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      {t("back")}
                    </Button>
                    <Button 
                      onClick={handleStep2Next}
                      size="lg" 
                      className="w-full"
                    >
                      {t("next")}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>

                  <LanguageSwitcher language={language} onToggle={toggleLanguage} className="mt-6" />
                </CardContent>
              </Card>
            )}

            {/* Step 3: Contact Details */}
            {step === 3 && (
              <Card className="border-none shadow-2xl animate-fade-in">
                <CardHeader className="pb-6">
                  <CardTitle className="text-2xl md:text-3xl mb-2">
                    {t("step3Title")}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {t("step3Description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("step3EmailLabel")}</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder={t("step3EmailPlaceholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">{t("step3PhoneLabel")}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder={t("step3PhonePlaceholder")}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={errors.phone ? "border-destructive" : ""}
                        minLength={9}
                        maxLength={20}
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive">{errors.phone}</p>
                      )}
                    </div>

                    <div className="flex items-start space-x-2 pt-2">
                      <Checkbox
                        id="consent"
                        checked={consent}
                        onCheckedChange={(checked) => setConsent(checked === true)}
                      />
                      <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
                        {t("step3ConsentText")}{" "}
                        <a href="/polityka-prywatnosci" className="text-primary hover:underline">
                          {t("step3PrivacyPolicy")}
                        </a>
                        . *
                      </Label>
                    </div>
                    {errors.consent && (
                      <p className="text-sm text-destructive">{errors.consent}</p>
                    )}
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button 
                      onClick={() => setStep(2)}
                      variant="outline"
                      size="lg"
                      className="w-full"
                    >
                      <ArrowLeft className="mr-2 h-5 w-5" />
                      {t("back")}
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      size="lg" 
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t("submitting") : t("submit")}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                  </div>

                  <LanguageSwitcher language={language} onToggle={toggleLanguage} className="mt-6" />
                </CardContent>
              </Card>
            )}


          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default SocialSellingAudit;
